import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { WsGlobalService } from '../http/ws-global.service';
import { CartStorageDto, CartTotalsDto, CartItemDto, ListMoneyDto, ListMoneyItemDto, CartCheckoutDto } from '../dto';
import { CartUtil } from '@ciptech/commons/utils';
import { DEFAULT_CART_STORAGE, DEFAULT_CART_TOTALS } from '../dto-defaults';
import { ObjectFactory } from '../utils';
import { GlobalService } from './global.service';
import { environment } from 'apps/ciptech/src/environments/environment';
import { AppStorageService } from './storage.service';
import { StorageKey } from '@ciptech/classes/storage.model';

@Injectable()
export class StoreService {
	cart$: Observable<CartStorageDto>;
	cartItems$: Observable<CartItemDto[]>;
	cartTotals$: Observable<CartTotalsDto>;
	moneySelected$: Observable<ListMoneyItemDto>;

	money: ListMoneyDto = {
		items: [
			{ IdMoneda: '01DOL', Simbolo: 'US$' },
			{ IdMoneda: '01SOL', Simbolo: 'S/' }
		],
		selected: null
	};

	constructor(
		private wsGlobal: WsGlobalService,
		private globalService: GlobalService,
		private appStorage: AppStorageService
	) {
		// Inicializar moneda seleccionada
		const initialMoney = this.loadMoneyFromStorage();
		this.money.selected = initialMoney;
		if (!this.appStorage.get<ListMoneyItemDto>(StorageKey.Money)) {
			this.appStorage.set(StorageKey.Money, initialMoney);
		}

		this.moneySelected$ = this.appStorage
			.select<ListMoneyItemDto>(StorageKey.Money)
			.pipe(
				map(value => value || this.money.items[0])
			);

		this.moneySelected$.subscribe(value => {
			this.money.selected = value;
		});

		// Inicializar carrito
		const initialCart = this.cartStorage;
		if (!this.appStorage.get<CartStorageDto>(StorageKey.Cart)) {
			this.appStorage.set(StorageKey.Cart, initialCart);
		}
		this.cart$ = this.appStorage
			.select<CartStorageDto>(StorageKey.Cart)
			.pipe(
				map(value => value || { ...DEFAULT_CART_STORAGE }),
				map(value => this.validateCartStorage(value))
			);
		this.cartItems$ = this.cart$.pipe(map(cart => cart.items || []));
		this.cartTotals$ = this.cart$.pipe(map(cart => cart.totals));
	}

	private loadMoneyFromStorage(): ListMoneyItemDto {
		const money = this.appStorage.get<ListMoneyItemDto>(StorageKey.Money);
		return money || this.money.items[0];
	}

	setMoneySelected(value: ListMoneyItemDto) {
		this.money.selected = value;
		this.appStorage.set(StorageKey.Money, value);
	}

	get moneySelectedValue(): ListMoneyItemDto {
		const money = this.appStorage.get<ListMoneyItemDto>(StorageKey.Money);
		return money || this.money.items[0];
	}

	// Cart options
	cartAdd(item: CartItemDto) {
		const storage = this.cartStorage;
		if (!storage.created) {
			storage.created = new Date();
		}
		if (!storage.totals) {
			storage.totals = { ...DEFAULT_CART_TOTALS };
			storage.totals.IdMonedaBase = this.moneySelectedValue?.IdMoneda || '01SOL';
		}
		else {
			storage.totals.IdMonedaBase = this.moneySelectedValue?.IdMoneda || '01SOL';
		}
		storage.items = storage.items || [];
		const index = storage.items.findIndex(x => x.IdArticulo === item.IdArticulo);
		if (index !== -1) {
			const existing = storage.items[index];
			const existingFactor = Number(existing.Factor) || 1;
			const newFactor = Number(item.Factor) || 1;

			// Cantidad total en unidad base
			const existingBaseQty = (Number(existing.Cantidad) || 0) * existingFactor;
			const newBaseQty = (Number(item.Cantidad) || 0) * newFactor;
			const totalBaseQty = existingBaseQty + newBaseQty;

			let merged: CartItemDto;
			if (existingFactor === 1) {
				merged = {
					...existing,
					Cantidad: totalBaseQty / existingFactor,
					PrecioBase: existing.PrecioBase ?? existing.Precio,
				};
			} else if (newFactor === 1) {
				merged = {
					...item,
					Cantidad: totalBaseQty / newFactor,
					PrecioBase: item.PrecioBase ?? item.Precio,
				};
			} else {
				merged = {
					...existing,
					Cantidad: totalBaseQty / existingFactor,
					PrecioBase: existing.PrecioBase ?? existing.Precio,
				};
			}
			storage.items[index] = merged;
		} else {
			storage.items.push(item);
		}
		storage.payment = null;
		this.cartSave(storage);
	}

	cartUpdateItem(item: CartItemDto): void {
		const storage = this.cartStorage;
		storage.items = storage.items || [];
		const index = storage.items.findIndex(
			x => x.IdArticulo === item.IdArticulo
		);
		// No existe y se intenta eliminar: no hay nada que modificar.
		if (item.Cantidad <= 0 && index === -1) {
			return;
		}
		if (index !== -1 && item.Cantidad > 0 && CartUtil.compareItem(item, storage.items[index])) {
			return;
		}

		if (!storage.created) {
			storage.created = new Date();
		}
		if (!storage.totals) {
			storage.totals = { ...DEFAULT_CART_TOTALS };
		}

		storage.totals.IdMonedaBase = this.moneySelectedValue?.IdMoneda || '01SOL';
		if (item.Cantidad <= 0) {
			storage.items.splice(index, 1);
		} else if (index !== -1) {
			storage.items[index] = item;
		} else {
			storage.items.push(item);
		}
		storage.checkout = null;
		storage.payment = null;
		storage.confirmation = null;
		storage.orderCulqi = null;

		this.cartSave(storage);
	}

	cartUpdate(item: CartItemDto) {
		const storage = this.cartStorage;
		storage.items = storage.items || [];
		const index = storage.items.findIndex(x => x.IdArticulo === item.IdArticulo);
		if (index !== -1) {
			const ocart = storage.items[index];
			ocart.Cantidad = item.Cantidad;
			ocart.IdUnidadMedida = item.IdUnidadMedida;
			ocart.UnidadMedida = item.UnidadMedida;
			storage.items[index] = ocart;
			storage.payment = null;
			this.cartSave(storage);
		}
	}

	get impuesto() {
		return environment.IGV_VALOR;
	}

	get cart(): CartStorageDto {
		const oCart = this.cartStorage;
		oCart.tc = this.globalService?.tc || 1;
		return oCart;
	}

	get cartItems(): CartItemDto[] {
		return this.cart?.items || [];
	}

	get cartStorage(): CartStorageDto {
		const storage = this.appStorage.get<CartStorageDto>(StorageKey.Cart);
		return this.validateCartStorage(storage);
	}

	private validateCartStorage(data: CartStorageDto | null): CartStorageDto {
		try {
			if (!data?.created)
				return { ...DEFAULT_CART_STORAGE };

			const diff = new Date().getTime() - new Date(data.created).getTime();
			// El carrito sólo es válido por 8 horas
			if (diff > 1000 * 60 * 60 * 8)
				throw new TypeError('cart expired');
			return this.buildCartStorage(data);
		} catch {
			this.appStorage.remove(StorageKey.Cart);
			return { ...DEFAULT_CART_STORAGE };
		}
	}

	get cartValue(): CartStorageDto {
		const cart = this.appStorage.get<CartStorageDto>(StorageKey.Cart);
		return this.validateCartStorage(cart);
	}

	get tc(): number {
		return this.globalService?.tc || 1;
	}

	cartRemoveCheckout() {
		var storage = this.cartStorage;
		if (storage.checkout && storage.checkout != null) {
			storage.checkout = null;
			storage.payment = null;
			this.cartSave(storage);
		}
	}

	cartItemRemove(id: number): boolean {
		const storage = this.cartStorage;
		// const items = this.cartItems;
		const index = storage.items.findIndex(x => x.IdArticulo === id);
		if (index !== -1) {
			storage.items.splice(index, 1);
			storage.payment = null;
			this.cartSave(storage);
			return !0;
		}
		return !1;
	}

	cartItemsClear() {
		let cartStorage = this.cartStorage;
		cartStorage.items = [];
		cartStorage.payment = null;
		this.cartSave(cartStorage);
	}

	cartClearObj(cart: CartStorageDto) {
		cart.items = [];
		cart.checkout = null;
		cart.confirmation = null;
		cart.orderCulqi = null;
		cart.payment = null;
		return cart;
	}

	cartFullClear() {
		let cartStorage = this.cartClearObj(this.cartStorage);
		this.cartSave(cartStorage);
	}

	cartClear() {
		this.appStorage.set(StorageKey.Cart, { ...DEFAULT_CART_STORAGE });
	}

	cartSave(storage: CartStorageDto): CartStorageDto {
		const calculated = this.calculateCart(storage);
		this.appStorage.set(StorageKey.Cart, { ...calculated });
		return calculated;
	}

	completeCartNullData(cartRef: CartStorageDto): CartStorageDto {
		const current = this.cart;
		return {
			created: cartRef.created ?? current.created,
			items: cartRef.items?.length ? cartRef.items : current.items,
			checkout: cartRef.checkout ?? current.checkout,
			user: cartRef.user ?? current.user,
			totals: cartRef.totals ?? current.totals,
			tc: cartRef.tc ?? current.tc,
			orderCulqi: cartRef.orderCulqi ?? current.orderCulqi,
			confirmation: cartRef.confirmation ?? current.confirmation,
			payment: cartRef.payment ?? current.payment
		};
	}

	// Aplicar TC
	cartTC(): Promise<any> {
		return new Promise((resolve, reject) => {
			this.wsGlobal.TasaCambio('body').subscribe(tc => {

				this.globalService.tc = tc;
				let cart = this.cartStorage;
				const moneda = this.moneySelectedValue;

				if (!cart) {
					resolve(cart);
					return;
				}

				cart.totals = cart.totals || {} as CartTotalsDto;
				cart.totals.IdMonedaBase = moneda.IdMoneda;

				cart = this.calculateCart(cart);

				if (Object.keys(cart).length) {
					this.cartSave(cart);
				}
				// console.log('Cambio de moneda global:', cart);
				resolve(cart);
			}, () => reject());
		});
	}

	public calculateCartItems(items: CartItemDto[], tc: number, monedaSeleccionada: string): { items: CartItemDto[]; subTotal: number } {
		let subTotal = 0;

		const updatedItems = items.map(item => {
			let precioBase = item.PrecioBase * (item.Factor || 1);

			precioBase = Math.round(precioBase * 100) / 100;
			let precioMoneda = precioBase;
			if (item.IdMonedaBase !== monedaSeleccionada) {
				if (item.IdMonedaBase === '01SOL' && monedaSeleccionada === '01DOL') {
					precioMoneda = precioBase / tc;
				} else if (item.IdMonedaBase === '01DOL' && monedaSeleccionada === '01SOL') {
					precioMoneda = precioBase * tc;
				}
			}
			precioMoneda = Math.round(precioMoneda * 100) / 100;
			subTotal += item.Cantidad * precioMoneda;
			return {
				...item,
				IdMoneda: monedaSeleccionada,
				Precio: precioMoneda
			};
		});
		subTotal = Math.round(subTotal * 100) / 100;
		return { items: updatedItems, subTotal };
	}

	public calculateCartCheckout(checkout: CartCheckoutDto, tc: number, monedaSeleccionada: string): { checkout: CartCheckoutDto; envio: number } {
		if (!checkout || checkout.IdFormaEnvio !== 2) {
			return { checkout, envio: 0 };
		}

		let envio = checkout.TarifaBase || 0;
		// Normalizar a base
		if (checkout.IdMonedaBase === '01SOL') {
			envio *= tc;
		}

		let envioTotal = envio;
		// Convertir a moneda seleccionada
		if (checkout.IdMonedaBase !== monedaSeleccionada) {
			if (checkout.IdMonedaBase === '01SOL' && monedaSeleccionada === '01DOL') {
				envioTotal *= (1 / tc);
			} else if (checkout.IdMonedaBase === '01DOL' && monedaSeleccionada === '01SOL') {
				envioTotal *= tc;
			}
		}
		envioTotal = envioTotal * (1 + this.impuesto);
		envioTotal = Math.round(envioTotal * 100) / 100;

		return {
			checkout: {
				...checkout,
				IdMonedaBase: monedaSeleccionada,
				Tarifa: envioTotal
			},
			envio: envioTotal
		};
	}

	public calculateCartTotals(subTotal: number, envio: number, monedaSeleccionada: string): CartTotalsDto {
		const round = (val: number) => Math.round(val * 100) / 100;

		const totals: CartTotalsDto = ObjectFactory.create<CartTotalsDto>(DEFAULT_CART_TOTALS);

		totals.IdMonedaBase = monedaSeleccionada;
		totals.SubTotal = subTotal;
		totals.Igv = subTotal * this.impuesto;
		totals.Envio = envio;

		totals.Total = totals.SubTotal + totals.Igv + totals.Envio;

		// Redondeo final
		totals.SubTotal = round(totals.SubTotal);
		totals.Igv = round(totals.Igv);
		totals.Envio = round(totals.Envio);
		totals.Total = round(totals.Total);

		return totals;
	}


	public calculateCart(storage: CartStorageDto): CartStorageDto {
		const oCart = { ...storage };

		if (!oCart.totals) {
			oCart.totals = { ...DEFAULT_CART_TOTALS };
			oCart.totals.IdMonedaBase = this.moneySelectedValue?.IdMoneda || '01SOL'; // cambio por merge
		}
		const monedaSeleccionada = oCart.totals.IdMonedaBase || this.moneySelectedValue?.IdMoneda || '01SOL';

		const tc = this.globalService?.tc || 1;
		// const totals: CartTotalsDto = ObjectFactory.create<CartTotalsDto>(DEFAULT_CART_TOTALS);
		oCart.tc = tc;

		// ITEMS
		const { items, subTotal } = this.calculateCartItems(
			oCart.items,
			tc,
			monedaSeleccionada
		);

		oCart.items = items;

		// CHECKOUT
		let envio = 0;
		if (oCart.checkout) {
			const result = this.calculateCartCheckout(
				oCart.checkout,
				tc,
				monedaSeleccionada
			);
			oCart.checkout = result.checkout;
			envio = result.envio;
		}

		// TOTALS
		oCart.totals = this.calculateCartTotals(
			subTotal,
			envio,
			monedaSeleccionada
		);
		oCart.created = new Date();

		return oCart;
	}

	buildCartStorage(data: any): CartStorageDto {
		const safeData: Partial<CartStorageDto> = {
			created: data?.created ? new Date(data.created) : undefined,
			items: Array.isArray(data?.items) ? data.items : [],
			checkout: data?.checkout ?? null,
			user: data?.user ?? null,
			totals: data?.totals
				? {
					IdMonedaBase: data.totals.IdMonedaBase || '01SOL',
					SubTotal: Number(data.totals.SubTotal) || 0,
					Envio: Number(data.totals.Envio) || 0,
					Igv: Number(data.totals.Igv) || 0,
					Total: Number(data.totals.Total) || 0
				}
				: undefined,
			orderCulqi: data?.orderCulqi ?? null,
			confirmation: data?.confirmation ?? null,
			payment: data?.payment ?? null
		};
		return ObjectFactory.create<CartStorageDto>(DEFAULT_CART_STORAGE, safeData);
	}

	public ValidacionCarrito(storage: CartStorageDto): { valid: boolean; message?: string } {
		// Validaciones listado items
		if (!storage.items || storage.items.length === 0) {
			return { valid: false, message: 'El carrito está vacío' };
		}

		if (storage.items.length < environment.LIM_MINIMO_PRODUCTOS) {
			return { valid: false, message: `El carrito debe contener al menos ${environment.LIM_MINIMO_PRODUCTOS} producto(s)` };
		}

		// Validaciones monto sub total
		let subTotalDolares = storage.totals.SubTotal;
		let minimoDolares = environment.MINIMO_COMPRA_DOL;
		let maximoDolares = environment.MAXIMO_COMPRA_DOL;
		const simboloMoneda = storage.totals.IdMonedaBase === '01SOL' ? 'S/' : '$';

		if (storage.totals.IdMonedaBase === '01SOL') {
			const tc = this.globalService?.tc || 1;
			minimoDolares = minimoDolares * tc;
			maximoDolares = maximoDolares * tc;
		}
		if (subTotalDolares < minimoDolares) {
			return { valid: false, message: `El costo de sus productos (Sin IGV) debe ser mayor a ${simboloMoneda}${minimoDolares.toFixed(2)}` };
		}
		if (subTotalDolares > maximoDolares) {
			return { valid: false, message: `El costo de sus productos (Sin IGV) no debe ser mayor a ${simboloMoneda}${maximoDolares.toFixed(2)}` };
		}

		return { valid: true };
	}
}
